/**
 * ============================================================================
 * O PROMPT DA FABRICA — uma copia so, no contrato
 * ============================================================================
 *
 * Este texto vivia inteiro em `criador-sites/lib/gemini.ts`, dentro do
 * navegador. Enquanto a geracao era so a esteira da aba aberta, isso bastava.
 *
 * Deixou de bastar quando o worker passou a gerar site: passariam a existir DUAS
 * versoes do mesmo prompt, uma no navegador e outra no servidor, divergindo a
 * cada ajuste. E divergencia de prompt nao da erro — da site diferente, e a
 * pessoa que ajustou o texto num lugar jura que ajustou nos dois.
 *
 * Entao ele mora aqui, ao lado dos templates que servem de queda quando a IA nao
 * responde. Quem chama o Gemini e sempre a API; o que este arquivo faz e montar
 * as palavras, e montar as palavras nao e segredo.
 *
 * O que NAO esta aqui, de proposito: as imagens. Elas sao base64 de dezenas de
 * KB cada e so existem no lado de quem tem os arquivos — o navegador da fabrica.
 * A funcao devolve o texto; quem tem imagem anexa por fora.
 */
/**
 * Direcao de arte por nicho.
 *
 * Existe porque "crie um site bonito" produz o mesmo site cinza para pizzaria e
 * para escritorio de advocacia. Casar por termo e grosseiro e funciona: o que se
 * quer evitar e o padrao unico, nao acertar o estilo com precisao.
 */
export function direcaoDeArte(categoria, nome) {
    const termo = `${categoria || ''} ${nome || ''}`.toLowerCase();
    if (termo.match(/restaurante|sushi|hamburguer|pizza|doce|caf(e|é)|bistr(o|ô)|gastronomia|padaria|food|lanche|confeitaria|bolo|torta/)) {
        return `
            ESTILO VISUAL: "APPETITE APPEAL & WARMTH"
            - Vibe: Acolhedora, deliciosa, vibrante ou rústica (dependendo se é chique ou fast-food).
            - Cores: Use tons quentes (laranjas, vermelhos, marrons, cremes) ou preto carvão para contraste. Evite azul padrão.
            - Formas: Bordas arredondadas (rounded-2xl ou rounded-full) para parecer "comestível" e amigável.
            - Layout: Destaque fotos gigantes dos pratos. Use cards com sombras suaves (shadow-xl).
            - Tipografia: Fontes com personalidade (serifadas para chique, arredondadas para casual).
        `;
    }
    if (termo.match(/tech|software|app|dev|ti|seguran(c|ç)a|gamer|cyber|eletr(o|ô)nico|digital|crypto|marketing|ag(e|ê)ncia/)) {
        return `
            ESTILO VISUAL: "FUTURE TECH & INNOVATION"
            - Vibe: Inovadora, rápida, confiável, high-end.
            - Design System: Use efeitos de vidro (backdrop-blur, bg-white/5) e gradientes sutis.
            - CORES: NÃO force modo escuro. Siga a paleta da logo do cliente. Se a logo for clara, use fundos claros com sombras puras.
            - Formas: Bordas levemente arredondadas (rounded-lg).
            - Layout: Use "Bento Grids" (grids assimétricos). Linhas finas de borda.
            - Tipografia: Sans-serif moderna, geométrica e limpa.
        `;
    }
    if (termo.match(/sa(u|ú)de|cl(i|í)nica|m(e|é)dic|dentista|est(e|é)tica|beleza|spa|pilates|yoga|terapia|psico/)) {
        return `
            ESTILO VISUAL: "PURE CLEAN & TRUST"
            - Vibe: Limpeza, calma, profissionalismo, elegância.
            - CORES: Use tons pastéis derivados da cor principal da logo. MUITO contraste e claridade.
            - Formas: Layouts arejados (muito whitespace/padding). Linhas suaves.
            - Layout: Minimalista. Imagens de alta qualidade inspirando bem-estar.
            - Tipografia: Serifada light para títulos ou Sans-serif fina e espaçada.
        `;
    }
    if (termo.match(/advogad|lei|jur(i|í)dico|cont(a|á)bil|financeiro|imobili(a|á)ria|consultoria|engenharia|constru(c|ç)(a|ã)o|arquitetura/)) {
        return `
            ESTILO VISUAL: "AUTHORITY & SOLIDITY"
            - Vibe: Confiança, tradição, solidez, premium.
            - CORES: Use tons sóbrios. Se a logo for dourada/azul, use essas cores como âncoras. Evite temas pretos genéricos; prefira Azul Marinho ou Cinza Profundo apenas se a marca pedir.
            - Formas: Retangulares e estruturadas. Pouco arredondamento (rounded-sm).
            - Layout: Grids estruturados, simétricos. Uso de divisores de seção sólidos.
            - Tipografia: Serifada para títulos (autoridade).
        `;
    }
    if (termo.match(/loja|roupa|moda|acess(o|ó)rio|venda|varejo|store|pet|animal/)) {
        return `
            ESTILO VISUAL: "MODERN RETAIL & BOLD"
            - Vibe: Urgência, desejo, clareza.
            - Cores: Alto contraste (Preto e Branco é clássico fashion) ou cores da marca vibrantes.
            - Formas: Botões grandes e clicáveis. Cards de produto limpos.
            - Layout: Foco total na imagem do produto. Galerias e carrosséis.
        `;
    }
    return `
        ESTILO VISUAL: "MODERN & UNIQUE BRANDING"
        - Vibe: Profissional, única e adaptada ao nome "${nome}".
        - Cores: Crie uma paleta harmônica que fuja do padrão "Bootstrap". Use cores complementares.
        - IMERSÃO DE MARCA: Não crie apenas detalhes coloridos. Crie uma atmosfera baseada na identidade visual. Se a logo tiver uma cor dominante vibrante, use-a em seções inteiras, em gradientes de fundo ou em tipografias de destaque. O site deve ser um espelho da força visual da marca.
        - Layout: Dinâmico, responsivo, com boa hierarquia visual.
    `;
}
/** A instrucao de sistema da fabrica. Vale para gerar e para editar. */
export const INSTRUCAO_SISTEMA_SITE = `
    ATUE COMO: Designer de Interface Premiado (Awwwards/Dribbble) E Especialista em Marketing Digital de Alta Performance.

    >>> MISSÃO <<<
    Criar uma Landing Page ÚNICA e IMPRESSIONANTE (nível R$ 1.000,00+).
    O site deve parecer feito sob medida, NUNCA genérico.

    >>> ESTRUTURA OBRIGATÓRIA (MÍNIMO 9 SEÇÕES) <<<
    Você DEVE incluir EXATAMENTE esta sequência com IDs correspondentes para navegação:
    1. HEADER (#inicio / #topo): Logo à esquerda, menu de navegação central com âncoras locais (#sobre, #servicos, #estrutura, #contato) e botão de orçamento/CTA com alto contraste e texto legível à direita.
    2. HERO (Impacto) (#inicio): Use uma foto REAL do cliente (do contexto visual). Headline deve focar no Problema + Solução imediata.
    3. SEÇÃO "DOR/PROBLEMA" (#problema): Mostre que entende profundamente o que o lead está passando. Use copy empática e direta.
    4. SEÇÃO "PARA QUEM" (#para-quem): Segmente o público (Ex: "Para empresas que...", "Para pais que buscam...").
    5. SOLUÇÃO DETALHADA (#servicos): O que exatamente é entregue, com autoridade e cards estruturados.
    6. SEÇÃO EXCLUSIVA POR NICHO (#estrutura / #diferenciais): Crie algo específico para este lead (ex: infraestrutura, metodologia, diferenciais).
    7. GALERIA REAL (#galeria): Use as fotos enviadas no contexto (IMG_X) para passar veracidade. Use layouts de bento-grid ou alvenaria.
    8. DEPOIMENTOS (GOOGLE REVIEWS) (#depoimentos): Use os nomes, estrelas e textos das reviews reais fornecidas no contexto.
    9. FAQ INTELIGENTE (#faq): Responda a 4-5 dúvidas que impedem a venda (quebra de objeção).
    10. RODAPÉ PROFISSIONAL (#contato): Mapa, endereço completo, telefone, horários e links.

    >>> REGRA ABSOLUTA DE NAVEGAÇÃO & ROTAS (ANTI-TELA-BRANCA) <<<
    1. NUNCA, SOB NENHUMA HIPÓTESE, use links de rotas externas como href="/sobre", href="/servicos", href="/contato" ou href="/estrutura". Isso quebra o site com tela branca!
    2. TODOS os links do menu do topo, rodapé e botões DEVEM usar âncoras locais com hash:
       - href="#inicio", href="#sobre", href="#servicos", href="#estrutura", href="#galeria", href="#depoimentos", href="#contato".
    3. Cada seção correspondente DEVE conter o id exato correspondente (ex: <section id="sobre">, <section id="servicos">, <section id="estrutura">, <section id="contato">).
    4. Adicione sempre a classe 'scroll-smooth' na tag <html> ou estilo global para rolagem suave ao clicar nos menus.
    5. Botões de ação e WhatsApp devem apontar para o link do WhatsApp fornecido ou href="#contato".

    >>> REGRAS CRÍTICAS DE DESIGN SYSTEM, CONTRASTE & LEGIBILIDADE (ANTI-TEXTO-INVISÍVEL) <<<
    1. LEI DO CONTRASTE EXTREMO:
       - Se uma seção tem fundo claro (branco, bege, cinza claro, azul claro, bg-white, bg-slate-50, bg-zinc-100):
         * TÍTULOS e HEADLINES: DEVE usar cor escura forte (ex: text-slate-900, text-zinc-900, text-gray-900 ou azul/marrom escuro profundo com alto contraste).
         * PARÁGRAFOS e TEXTOS DE APOIO: DEVE usar cor escura legível (ex: text-slate-700, text-zinc-600, text-gray-700).
         * É EXPRESSAMENTE PROIBIDO usar texto claro, pastel ou apagado em fundo claro (ex: text-blue-200, text-cyan-200, text-white, text-slate-300 em fundo branco É ERRO GRAVE).
       - Se uma seção tem fundo escuro (preto, azul marinho, grafite):
         * TÍTULOS: text-white.
         * PARÁGRAFOS: text-slate-200 ou text-zinc-300.
    2. BOTÕES DE AÇÃO (CTAs) E LINKS DO HEADER:
       - O botão do topo e botões de chamada DEVEM ter fundo com contraste nítido (ex: bg-slate-900 text-white, ou bg-emerald-600 text-white, ou cor de destaque da marca com texto branco text-white ou preto text-slate-900 garantindo contraste perfeito).
       - NUNCA use botão branco ou transparente com texto branco em fundo claro.
       - Links do menu de navegação no header devem ser nítidos e escuros (ex: text-slate-700 hover:text-slate-900 font-medium).
    3. DNA DA MARCA:
       - Identifique as cores predominantes da logo ou das fotos da fachada/banners. Use essas cores para detalhes nobres, botões sólidos, ícones, bordas de cards e faixas de destaque, mas NUNCA comprometa a legibilidade do texto.
    4. MOBILE FIRST: Escreva CSS pensando primeiro no celular e expanda com 'md:'.
    5. INTERATIVIDADE: Use transições suaves em hover nos botões e links.

    >>> REGRAS DE IMAGENS REAIS (CRÍTICO) <<<
    Você DEVE usar os PLACEHOLDERS abaixo nas suas tags <img>. NÃO use Unsplash se houver fotos reais.
    1. Para a LOGO (WhatsApp Perfil): Use src="[LOGO]"
    2. Para as FOTOS LOCAIS (ZIP/PASTA): Use src="[IMG_0]", src="[IMG_1]", src="[IMG_2]", etc.
    3. Para as FOTOS REMOTAS (Google/Links): Use src="[REMOTE_IMG_0]", src="[REMOTE_IMG_1]", etc. (Use estas se as LOCAIS acabarem ou forem insuficientes).
    MANTENHA A ORDEM: Tente usar as imagens locais para seções de portfólio/galeria primeiro.
`;
/** Fecho da instrucao de geracao. Separado porque `editar-site` usa outro. */
const OBJETIVO_GERAR = `
        OBJETIVO: Criar uma Landing Page do zero baseada no prompt abaixo.
        FERRAMENTA: Use Tailwind CSS via CDN (já injetado).
        SAÍDA: Apenas código HTML válido (com <!DOCTYPE html>, <html>, <head>, <body>). Sem markdown.
    `;
/**
 * Normaliza telefone para link do WhatsApp.
 *
 * Nao reaproveita `numeroWhatsapp` de `templates.ts` de proposito: la o numero
 * vai para um campo do spec, aqui vai para dentro de uma URL — e um numero vazio
 * tem de virar `#`, nunca `https://wa.me/55`, que abre o WhatsApp em branco.
 */
function linkWhatsapp(telefone) {
    const digitos = (telefone ?? '').replace(/\D/g, '');
    return digitos ? `https://wa.me/55${digitos}` : '#';
}
/**
 * Monta o texto que vai ao modelo.
 *
 * Tres cenarios, na mesma ordem da versao que rodava no navegador: com blueprint
 * (roteiro pronto), com dados de varredura mas sem roteiro, e o manual. A ordem
 * importa — o blueprint e a informacao mais especifica que existe, e desprezar
 * um roteiro que a captacao ja pagou para produzir seria jogar dinheiro fora.
 */
export function montarPromptDeSite(d) {
    const whats = linkWhatsapp(d.telefone);
    const direcao = direcaoDeArte(d.categoria, d.nome);
    const servicos = d.servicos && d.servicos.length > 0 ? d.servicos.join(', ') : 'Serviços Gerais';
    let corpo;
    if (d.blueprint && d.blueprint.length > 0) {
        corpo = `
            CLIENTE: ${d.nome}
            RAMO: ${d.categoria}
            ENDEREÇO: ${d.endereco ?? ''}

            ${direcao}

            MAPA DE CONTEÚDO (Distribua inteligentemente nos IDs #home, #about, #services, etc):
            ${d.blueprint
            .map((s, i) => `
                SEÇÃO ${i + 1} (${s.titulo}):
                - Conteúdo: ${s.descricao}
                - Sugestão UX do Blueprint: ${s.estrategiaUx}
            `)
            .join('\n')}

            Use IDs: #home, #about, #services, #social-proof, #contact.
            CTA Link (Botões): ${whats}

            NOTA: Incorpore os dados reais (endereço, telefone, horário) no rodapé ou seção de contato.
        `;
    }
    else if (d.resumo || d.endereco || (d.servicos && d.servicos.length > 0)) {
        const depoimentos = d.avaliacoes && d.avaliacoes.length > 0
            ? `Use estes depoimentos REAIS (ignore depoimentos automáticos/inventados): ${d.avaliacoes
                .map((r) => `${r.autor}: "${r.texto}" (${r.nota ?? 5} estrelas)`)
                .join(' | ')}`
            : `Crie 3 depoimentos fictícios elogiando o serviço, baseados na nota ${d.nota ?? 5.0} estrelas.`;
        corpo = `
            MODO DE IMPROVISO CRIATIVO (SEM BLUEPRINT):

            CLIENTE: ${d.nome}
            RAMO/INDÚSTRIA: ${d.categoria}
            ESPECIALIDADES/TAGS: ${servicos}
            RESUMO DO NEGÓCIO: ${d.resumo || 'Empresa local de alta qualidade.'}
            ENDEREÇO: ${d.endereco ?? ''}
            HORÁRIO: ${d.horarios ?? ''}

            ${direcao}

            >>> SUA MISSÃO <<<
            Como não tenho um roteiro "blueprint", você tem LIBERDADE TOTAL para estruturar o site da melhor forma para este nicho (${d.categoria}).

            Estrutura Sugerida (mas pode adaptar):
            1. Hero: Título impactante usando o nome "${d.nome}" e uma promessa de valor baseada em "${servicos}".
            2. Sobre: Use o resumo "${d.resumo ?? ''}" para criar uma seção de autoridade.
            3. Serviços: Crie cards para os principais serviços listados em: ${servicos}.
            4. Prova Social: ${depoimentos}
            5. Contato/Rodapé: Inclua endereço, horário e botão de WhatsApp (${whats}).

            Use IDs de navegação padrão (#home, #about, #services, #contact).
        `;
    }
    else {
        corpo = `
            PROJETO MANUAL:
            Cliente: ${d.nome}
            Ramo: ${d.categoria}
            Briefing: ${d.escopo ?? ''}

            ${direcao}

            Crie um site completo, responsivo e com design premium. CTA Link: ${whats}.
        `;
    }
    let texto = `${corpo}\n\n${INSTRUCAO_SISTEMA_SITE}\n${OBJETIVO_GERAR}`;
    if (d.temImagens && d.fotosSoPorLink) {
        /*
          As fotos existem, o modelo nao as ve.
    
          Instrucoes so sobre COLOCACAO — quantos marcadores ha e onde eles fazem
          sentido. Nenhum pedido de analise visual: o que ele nao viu, ele inventa.
        */
        const remotas = d.imagensRemotas ?? 0;
        let visao = `
            >>> FOTOS REAIS DO CLIENTE (USO OBRIGATÓRIO) <<<
            Este cliente TEM fotos reais, já hospedadas. Elas NÃO estão anexadas
            a esta conversa: você não vai vê-las, e não deve descrever, analisar
            nem citar o conteúdo delas no texto do site. O que você faz é colocar
            os marcadores abaixo nas tags <img>, nos lugares certos — o sistema
            troca cada marcador pela foto depois da sua resposta.
        `;
        if (d.temLogo) {
            visao += `\nLOGO: use src="[LOGO]" no cabeçalho, ao lado do nome. É a foto de perfil do negócio.`;
        }
        else {
            visao += `\nNÃO use o marcador [LOGO]: não há logo.`;
        }
        if (remotas > 0) {
            visao += `\nFOTOS: marcadores [REMOTE_IMG_0] até [REMOTE_IMG_${remotas - 1}] — ${remotas} no total. Não invente outros.
            - Use a PRIMEIRA no hero ou na seção "Sobre": no export ela costuma ser a fachada.
            - Espalhe as demais entre "Serviços" e "Galeria", uma por card.
            - Use TODAS: foto real hospedada que não entra no site é trabalho jogado fora.
            - Não repita a mesma foto em seções diferentes.`;
        }
        else {
            visao += `\nNÃO use [REMOTE_IMG_n] nem [IMG_n]: só há a logo.`;
        }
        visao += `\nNÃO use os marcadores [IMG_n]: não há foto local nesta geração.
            Onde precisar de imagem ALÉM dessas, use https://images.unsplash.com com um termo coerente com o ramo.
            A paleta você escolhe pelo RAMO e pelo texto do negócio — não por uma foto que você não viu.`;
        texto = `${visao}\n${texto}`;
    }
    else if (d.temImagens) {
        const locais = d.imagensLocais ?? 0;
        const remotas = d.imagensRemotas ?? 0;
        let visao = `
            >>> ANÁLISE DE IDENTIDADE VISUAL (VITAL) <<<
            Analise as imagens anexadas abaixo para criar o design:
        `;
        if (d.temLogo) {
            visao += `\nIMAGEM 1 (PERFIL): Analise se é uma LOGO ou FOTO PESSOAL.
                - Se tiver características de logotipo (vetores, texto estilizado, ícone centralizado), você DEVE obrigatoriamente usar src='[LOGO]' no Header.
                - Se for uma foto pessoal, use-a apenas como elemento de confiança na seção 'Sobre' ou 'Hero' (sem o marcador [LOGO] no header, apenas o nome em texto).
                - Identifique as cores HEX predominantes e use-as para criar a imersão de marca no site.`;
        }
        if (locais > 0) {
            visao += `\nDEMAIS IMAGENS: Observe o conteúdo dessas fotos.
            - Se vir fotos de serviços sendo realizados, use-as na seção "Serviços".
            - Se vir fotos do local físico, use na seção "Sobre".
            - Se vir fotos de produtos, use na "Galeria/Acervo".
            - Extraia a "vibe" (se é rústico, moderno, hospitalar, industrial) e aplique ao design.`;
        }
        /*
          Dizer QUANTOS marcadores existem, e nao so que existem.
    
          Sem isto o modelo escreve `[IMG_7]` quando so ha tres fotos, o marcador nao
          casa com nada na substituicao e o site publica com a string literal
          "[IMG_7]" dentro do src. Quebra visivel, causa invisivel.
        */
        if (locais > 0)
            visao += `\nMarcadores locais disponíveis: [IMG_0] até [IMG_${locais - 1}]. Não invente outros.`;
        if (remotas > 0)
            visao += `\nMarcadores remotos disponíveis: [REMOTE_IMG_0] até [REMOTE_IMG_${remotas - 1}]. Não invente outros.`;
        if (!d.temLogo)
            visao += `\nNÃO use o marcador [LOGO]: não há logo enviada.`;
        visao += `\n\nInstrução técnica Final: Você DEVE usar cores que harmonizem perfeitamente com essas fotos. Não use temas genéricos.`;
        texto = `${visao}\n${texto}`;
    }
    else {
        texto = `
            >>> ALERTA DE ORIGINALIDADE MÁXIMA <<<
            Este cliente NÃO possui foto de referência.
            Crie uma identidade visual EXCLUSIVA para ele. Proibido usar designs padrão de ${d.categoria}.
            Surpreenda com uma paleta de cores sofisticada e um layout que pareça feito sob medida por uma agência de luxo.

            NÃO use os marcadores [LOGO], [IMG_n] nem [REMOTE_IMG_n]: não há imagem nenhuma anexada.
            Onde precisar de imagem, use https://images.unsplash.com com um termo coerente com o ramo.

            ${texto}
        `;
    }
    return texto;
}
/**
 * Tira a conversa em volta e devolve so o documento — ou VAZIO, se nao houver um.
 *
 * O modelo, mesmo instruido a nao explicar, as vezes abre com "Claro! Aqui esta"
 * e fecha com "Espero que ajude". Publicar isso e publicar a frase renderizada
 * como texto solto acima do site.
 *
 * DEVOLVER VAZIO QUANDO NAO HA HTML NAO E DETALHE.
 *
 * Esta funcao alimenta o campo `html` de `respostaIaSchema`, e o copiloto do
 * editor faz literalmente `if (resposta.html) pushToHistory(resposta.html)`.
 * Uma versao que devolvesse o texto solto "limpo" quando nao ha HTML faria uma
 * resposta em prosa — de `texto-livre` ou de `sugestoes` — SUBSTITUIR o site
 * publicado do cliente pela frase da IA. O contrato promete "o bloco ```html,
 * quando houver"; o "quando houver" e o que esta linha cumpre.
 */
export function extrairHtmlDaResposta(texto) {
    if (!texto)
        return '';
    // 1. O bloco cercado, que e o que a instrucao de sistema pede.
    const bloco = texto.match(/```html([\s\S]*?)```/);
    let limpo = bloco?.[1] ?? texto.replace(/```html/g, '').replace(/```/g, '');
    // 2. Corta o que vier antes do documento. Sem documento, nao ha HTML: devolve
    //    vazio em vez de entregar a prosa como se fosse pagina.
    const doctype = limpo.search(/<!DOCTYPE html/i);
    if (doctype >= 0) {
        limpo = limpo.substring(doctype);
    }
    else {
        const tagHtml = limpo.search(/<html[\s>]/i);
        if (tagHtml >= 0)
            limpo = limpo.substring(tagHtml);
        else if (!bloco)
            return '';
        // Com bloco ```html mas sem <html>, e um FRAGMENTO. Quem chama decide o que
        // fazer com ele — os tres chamadores conferem `<html` antes de publicar.
    }
    const fim = limpo.search(/<\/html>/i);
    if (fim >= 0)
        limpo = limpo.substring(0, fim + 7);
    return limpo.trim();
}
/**
 * Troca os marcadores pelas imagens de verdade.
 *
 * O passo final tambem VARRE o que sobrou: um marcador que o modelo inventou e
 * que nao tem imagem correspondente vira `src` quebrado no site publicado. Aqui
 * ele e trocado por vazio e a tag some do fluxo em vez de virar o icone de imagem
 * quebrada do navegador.
 */
export function aplicarImagensNoHtml(html, imagens) {
    let saida = html;
    /*
      A substituicao vai por FUNCAO, nao por string.
  
      `String.replace` interpreta `$&`, `` $` ``, `$'`, `$1` e `$$` dentro do texto
      de substituicao. Uma URL remota com `$` — comum em URL assinada — sairia
      corrompida dentro do `src`, e o sintoma seria uma imagem quebrada sem
      nenhuma pista de por que.
    */
    const por = (valor) => () => valor;
    if (imagens.logo)
        saida = saida.replace(/\[LOGO\]/gi, por(imagens.logo));
    (imagens.locais ?? []).forEach((dado, i) => {
        saida = saida.replace(new RegExp(`\\[IMG_${i}\\]`, 'gi'), por(dado));
    });
    (imagens.remotas ?? []).forEach((url, i) => {
        saida = saida.replace(new RegExp(`\\[REMOTE_IMG_${i}\\]`, 'gi'), por(url));
    });
    // O que sobrou nao tem dono. Some a tag inteira, nao so o marcador: um
    // `<img src="">` ainda ocupa espaco e pinta a borda de imagem quebrada.
    saida = saida.replace(/<img[^>]*src=["']?\[(?:LOGO|IMG_\d+|REMOTE_IMG_\d+)\]["']?[^>]*>/gi, '');
    saida = saida.replace(/\[(?:LOGO|IMG_\d+|REMOTE_IMG_\d+)\]/gi, '');
    return saida;
}
/**
 * O spec de um documento HTML completo vindo da IA.
 *
 * `htmlBruto` era a escotilha do acervo importado, e o comentario do schema diz
 * "sites novos nao devem usar isto". A regra continua de pe para o que o WORKER
 * produz por template — spec tipado, secao por secao. O que muda e que a IA
 * devolve um documento pronto, e nao existe forma honesta de espremer um
 * documento inteiro nas oito secoes tipadas sem reescreve-lo.
 *
 * Por isso `origem` ganhou o valor `'ia'`: o spec continua dizendo a verdade
 * sobre o que ele guarda, e `VersaoSite.geradoPor` diz qual modelo produziu.
 * O renderizador ja serve documento completo como esta (sem embrulhar), entao o
 * `content_hash` continua valendo e republicar o identico continua nao escrevendo
 * no R2.
 */
export function specDeHtmlDaIa(html, negocio) {
    const digitos = (negocio.telefone ?? '').replace(/\D/g, '');
    return {
        versaoSpec: 1,
        template: 'ia-html',
        // Sem efeito: a unica secao e um documento completo, servido como esta.
        leiaute: 'classico',
        negocio: {
            nome: negocio.nome,
            categoria: negocio.categoria ?? '',
            telefone: negocio.telefone ?? '',
            whatsapp: digitos,
            endereco: negocio.endereco ?? '',
        },
        paleta: { primaria: '#1F4E79', secundaria: '#16324F', fundo: '#FFFFFF', texto: '#1A1A1A' },
        tipografia: { titulo: 'Inter', corpo: 'Inter', escala: 'normal' },
        seo: {
            titulo: negocio.categoria ? `${negocio.nome} — ${negocio.categoria}` : negocio.nome,
            descricao: '',
            palavrasChave: [],
            indexavel: true,
        },
        secoes: [{ tipo: 'htmlBruto', html, origem: 'ia' }],
        cssExtra: '',
    };
}
//# sourceMappingURL=prompt-site.js.map